<script lang="ts">
    import type { Field, Lead, CulmexJobs, SiteSurvey } from '@/types/types';
    import type Blocks from '@/schemas/public/Blocks';
    import type { DependencyMap } from '@/types/fieldDependency';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import { Button } from '@/components/ui/button';
    import { Badge } from '@/components/ui/badge';
    import { toast } from 'svelte-sonner';
    import { useForm } from '@/lib/precognition.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { evaluateFieldDependencies } from '@/lib/fieldDependencies';
    import { get } from 'svelte/store';
    import { formatDateLocale } from '@/lib/dateUtils';
    import Input from '@/components/Form/Inputs/Input.svelte';
    import Select from '@/components/Inputs/Select.svelte';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import PicklistBadge from '@/components/ui/picklist-badge/PicklistBadge.svelte';
    import { Pencil, CheckCircle2, CircleX } from 'lucide-svelte';

    type RecordType = 'lead' | 'job';

    type Props = {
        record: Lead | CulmexJobs;
        recordType: RecordType;
        siteSurveyBlocks: (Blocks & { fields: Field[] })[];
        siteSurveyDependencyMap?: DependencyMap;
        compact?: boolean;
        incomplete?: boolean;
        onRecordUpdated?: (updatedRecord: any) => void;
    };

    let {
        record,
        recordType,
        siteSurveyBlocks,
        siteSurveyDependencyMap = {},
        compact = false,
        incomplete = false,
        onRecordUpdated
    }: Props = $props();

    const siteSurvey = $derived((record as any).site_survey);

    let isEditing = $state(false);

    function buildValues(): Record<string, any> {
        const values: Record<string, any> = {};
        if (siteSurvey?.id) {
            values.site_survey_id = siteSurvey.id;
        }
        for (const block of siteSurveyBlocks) {
            for (const field of block.fields) {
                values[field.column_name] = siteSurvey?.[field.column_name as keyof SiteSurvey] ?? null;
            }
        }
        return values;
    }

    const storeUrl = route('site-survey.upsert');
    const form = useForm(
        { ...buildValues(), record_type: recordType, record_id: record.id } as any,
        'post',
        storeUrl,
    );

    function isFieldVisible(field: Field, values: Record<string, any>): boolean {
        const fieldState = evaluateFieldDependencies(
            field.column_name,
            siteSurveyDependencyMap,
            values,
            null
        );
        return fieldState.visible;
    }

    function syncFormToRecord() {
        const fresh = { ...buildValues(), record_type: recordType, record_id: record.id };
        const f = get(form);
        f.defaults(fresh as any);
        f.reset();
    }

    function startEditing() {
        syncFormToRecord();
        get(form).clearErrors();
        isEditing = true;
    }

    function cancelEditing() {
        get(form).reset();
        get(form).clearErrors();
        isEditing = false;
    }

    function handleSubmit() {
        $form.post(route('site-survey.upsert'), {
            preserveScroll: true,
            onSuccess: (page: any) => {
                isEditing = false;
                toast.success('Site survey updated');
                const flash = page?.props?.flash;
                const updated = flash?.updatedLead ?? flash?.updatedJob;
                if (updated && onRecordUpdated) {
                    onRecordUpdated(updated);
                }
                syncFormToRecord();
            },
            onError: () => {
                toast.error('Failed to save site survey. Please check the form for errors.');
            },
        });
    }

    const hasSiteSurveyData = $derived(siteSurvey !== null && siteSurvey !== undefined);

    // Flat lookup: column_name → Field
    const fieldsByColumn = $derived.by(() => {
        const map: Record<string, Field> = {};
        for (const block of siteSurveyBlocks) {
            for (const f of block.fields) {
                map[f.column_name] = f;
            }
        }
        return map;
    });

    // parent column → [child columns] (visibility dependencies only)
    const childrenOf = $derived.by(() => {
        const map: Record<string, string[]> = {};
        for (const [col, deps] of Object.entries(siteSurveyDependencyMap)) {
            for (const dep of deps) {
                if (dep.type === 'visibility') {
                    (map[dep.sourceFieldColumn] ??= []).push(col);
                }
            }
        }
        return map;
    });

    // Set of field columns that are children of another field
    const isChildField = $derived(new Set(Object.values(childrenOf).flat()));

    // ── View mode: domain-specific layout ────────────────────────────────────

    // Scaffold face cards
    const SCAFFOLD_FACES = [
        { label: 'Front', required: 'front_required', width: 'front_width', height: 'front_height' },
        { label: 'Back', required: 'back_required', width: 'back_width', height: 'back_height' },
        { label: 'Right side', required: 'right_side_required', width: 'right_side_width', height: 'right_side_height' },
        { label: 'Left side', required: 'left_side_required', width: 'left_side_width', height: 'left_side_height' },
        { label: 'Other areas', required: 'other_areas_required', width: 'other_areas_width', height: 'other_areas_height' },
    ];

    // Sectioned view columns (excluded from the general top-fields loop)
    const VIEW_SECTIONS = [
        {
            label: 'Hazards & site conditions',
            columns: ['install_obstructions', 'special_requirements', 'power_cables_present', 'power_cables_notes', 'hazardous_materials_present', 'hazardous_materials_notes'],
        },
        {
            label: 'Access & logistics',
            columns: ['minimum_access_width', 'distance_to_parking', 'distance_from_yard_miles', 'distance_from_yard_time', 'travel_access_notes', 'total_parameter'],
        },
        {
            label: 'Job planning',
            columns: ['lifts_required', 'job_sketch_notes', 'job_needs_up_by', 'job_duration', 'customer_needs_to_know'],
        },
    ];

    function getSelectLabel(field: Field, value: any): string {
        if (value == null || value === '') return '—';
        try {
            const opts = parseFieldOptions(field);
            const match = opts.find((o: { value: any }) => String(o.value) === String(value));
            return match ? match.label : String(value);
        } catch {
            return String(value);
        }
    }

    function formatMeterValue(label: string, value: any): string {
        if (value == null || value === '') return '—';
        if (label.trim().endsWith('(m)')) return `${value}m`;
        return String(value);
    }

    const SECTIONED_COLUMNS = new Set([
        ...SCAFFOLD_FACES.flatMap(f => [f.required, f.width, f.height]),
        'sides_notes',
        ...VIEW_SECTIONS.flatMap(s => s.columns),
    ]);
</script>

<FormBlock
    label="Site Survey"
    labelClass={incomplete ? 'text-destructive' : ''}
    collapsible={true}
    defaultOpen={true}
    id="section-site-survey"
>
    {#snippet buttons()}
        {#if hasSiteSurveyData}
            <CheckCircle2 class="h-5 w-5 self-center" style="color: var(--success)" />
        {:else}
            <CircleX class="h-5 w-5 self-center" style="color: var(--destructive)" />
        {/if}
        {#if !isEditing}
            <Button size="sm" variant={incomplete ? 'destructive' : 'outline'} onclick={startEditing}>
                <Pencil class="mr-1 h-4 w-4" />
                Edit
            </Button>
        {:else}
            <div class="flex gap-2">
                {#if $form.isDirty}
                    <Button size="sm" onclick={handleSubmit} disabled={$form.processing}>
                        {$form.processing ? 'Saving...' : 'Save Changes'}
                    </Button>
                {/if}
                <Button size="sm" variant="outline" onclick={cancelEditing} disabled={$form.processing}>
                    Cancel
                </Button>
            </div>
        {/if}
    {/snippet}

    {#if isEditing}
        <!-- ── Edit mode: dynamic field loop with grouped checkbox rows ── -->
        {#each siteSurveyBlocks as block (block.id)}
            {#each block.fields as field (field.id)}
                {#if field.editable && !isChildField.has(field.column_name) && isFieldVisible(field, $form)}
                    {#if childrenOf[field.column_name]?.length > 0}
                        <!-- Parent checkbox with dependents below -->
                        <div class="col-span-2 flex flex-col gap-2">
                            <Checkbox
                                bind:checked={$form[field.column_name]}
                                label={field.label}
                                error={$form.errors[field.column_name]}
                            />
                            <div
                                class="grid gap-4"
                                style="grid-template-columns: repeat({childrenOf[field.column_name].length}, 1fr)"
                            >
                                {#each childrenOf[field.column_name] as childCol (childCol)}
                                    {#if fieldsByColumn[childCol]?.editable && isFieldVisible(fieldsByColumn[childCol], $form)}
                                        {@const childField = { ...fieldsByColumn[childCol], required: !!$form[field.column_name] }}
                                        <div>
                                            {#if childField.ui_type === 'select'}
                                                <Select
                                                    label={childField.label}
                                                    bind:value={$form[childCol]}
                                                    field={childField}
                                                    error={$form.errors[childCol]}
                                                />
                                            {:else if childField.ui_type === 'textarea'}
                                                <Textarea
                                                    label={childField.label}
                                                    field={childField}
                                                    bind:value={$form[childCol]}
                                                    error={$form.errors[childCol]}
                                                />
                                            {:else if childField.ui_type === 'datetime'}
                                                <DateTime
                                                    label={childField.label}
                                                    bind:value={$form[childCol]}
                                                    error={$form.errors[childCol]}
                                                />
                                            {:else}
                                                <Input
                                                    field={childField}
                                                    bind:value={$form[childCol]}
                                                    error={$form.errors[childCol]}
                                                    onkeydown={() => {}}
                                                />
                                            {/if}
                                        </div>
                                    {/if}
                                {/each}
                            </div>
                        </div>
                    {:else}
                        {#if field.ui_type === 'checkbox'}
                            <Checkbox
                                bind:checked={$form[field.column_name]}
                                label={field.label}
                                error={$form.errors[field.column_name]}
                            />
                        {:else if field.ui_type === 'select'}
                            <Select
                                label={field.label}
                                bind:value={$form[field.column_name]}
                                {field}
                                error={$form.errors[field.column_name]}
                            />
                        {:else if field.ui_type === 'textarea'}
                            <Textarea
                                label={field.label}
                                {field}
                                bind:value={$form[field.column_name]}
                                error={$form.errors[field.column_name]}
                            />
                        {:else if field.ui_type === 'datetime'}
                            <DateTime
                                label={field.label}
                                bind:value={$form[field.column_name]}
                                error={$form.errors[field.column_name]}
                            />
                        {:else}
                            <Input
                                {field}
                                bind:value={$form[field.column_name]}
                                error={$form.errors[field.column_name]}
                                onkeydown={() => {}}
                            />
                        {/if}
                    {/if}
                {/if}
            {/each}
        {/each}

        <!-- Bottom save button — only shown when there are unsaved changes -->
        {#if $form.isDirty}
            <div class="col-span-2 flex justify-end gap-2 pt-2">
                <Button size="sm" onclick={handleSubmit} disabled={$form.processing}>
                    {$form.processing ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button size="sm" variant="outline" onclick={cancelEditing} disabled={$form.processing}>
                    Cancel
                </Button>
            </div>
        {/if}
    {:else}
        <!-- ── View mode: structured section layout ── -->

        <!-- General top fields (workforce access, vehicle access, etc.) -->
        <div class="col-span-2">
            <div class="mb-3 flex items-center gap-3">
                <span class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">General</span>
                <div class="h-px flex-1 bg-border"></div>
            </div>
            <div class={compact ? 'grid grid-cols-2 gap-x-6 gap-y-3' : 'grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-3 xl:grid-cols-4'}>
                {#each siteSurveyBlocks as block (block.id)}
                    {#each block.fields as field (field.id)}
                        {#if field.editable && !SECTIONED_COLUMNS.has(field.column_name)}
                            <div>
                                <span class="font-medium">{field.label}:</span>
                                <div>
                                    {#if field.ui_type === 'select'}
                                        {#if field.value_color && field.value_label}
                                            <PicklistBadge label={field.value_label} color={field.value_color} />
                                        {:else}
                                            {getSelectLabel(field, $form[field.column_name])}
                                        {/if}
                                    {:else if field.ui_type === 'datetime'}
                                        {formatDateLocale($form[field.column_name]) ?? '—'}
                                    {:else}
                                        {formatMeterValue(field.label, $form[field.column_name])}
                                    {/if}
                                </div>
                            </div>
                        {/if}
                    {/each}
                {/each}
            </div>
        </div>

        <!-- Scaffold faces -->
        <div class="col-span-2">
            <div class="mb-2 flex items-center gap-3">
                <span class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Scaffold faces</span>
                <div class="h-px flex-1 bg-border"></div>
            </div>
            <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {#each SCAFFOLD_FACES as face (face.required)}
                    <div class="rounded-lg border p-3">
                        <div class="mb-3 flex flex-wrap items-start justify-between gap-1">
                            <span class="text-sm font-medium">{face.label}</span>
                            <Badge
                                variant={$form[face.required] ? 'default' : 'secondary'}
                                class="whitespace-nowrap text-xs"
                            >
                                {$form[face.required] ? 'Required' : 'Not required'}
                            </Badge>
                        </div>
                        <div class="space-y-1 text-sm">
                            <div class="flex justify-between gap-2">
                                <span class="text-muted-foreground">Width (m)</span>
                                <span class={$form[face.required] ? 'font-medium' : 'text-muted-foreground'}>
                                    {formatMeterValue('Width (m)', $form[face.width])}
                                </span>
                            </div>
                            <div class="flex justify-between gap-2">
                                <span class="text-muted-foreground">Height (m)</span>
                                <span class={$form[face.required] ? 'font-medium' : 'text-muted-foreground'}>
                                    {formatMeterValue('Height (m)', $form[face.height])}
                                </span>
                            </div>
                        </div>
                    </div>
                {/each}
            </div>
            {#if $form.sides_notes}
                <div class="mt-3">
                    <span class="font-medium">Sides Notes:</span>
                    <p>{$form.sides_notes}</p>
                </div>
            {/if}
        </div>

        <!-- Remaining sections -->
        {#each VIEW_SECTIONS as section (section.label)}
            <div class="col-span-2">
                <div class="mb-3 flex items-center gap-3">
                    <span class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{section.label}</span>
                    <div class="h-px flex-1 bg-border"></div>
                </div>
                <div class={compact ? 'grid grid-cols-2 gap-x-6 gap-y-3' : 'grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-3 xl:grid-cols-4'}>
                    {#each section.columns as col (col)}
                        {#if fieldsByColumn[col]?.editable}
                            {@const field = fieldsByColumn[col]}
                            <div>
                                <span class="font-medium">{field.label}:</span>
                                <div>
                                    {#if field.ui_type === 'checkbox'}
                                        <Badge variant={$form[col] ? 'default' : 'secondary'} class="mt-1">
                                            {$form[col] ? 'Yes' : 'No'}
                                        </Badge>
                                    {:else if field.ui_type === 'datetime'}
                                        {formatDateLocale($form[col]) ?? '—'}
                                    {:else if field.ui_type === 'select'}
                                        {#if field.value_color && field.value_label}
                                            <PicklistBadge label={field.value_label} color={field.value_color} />
                                        {:else}
                                            {getSelectLabel(field, $form[col])}
                                        {/if}
                                    {:else}
                                        {formatMeterValue(field.label, $form[col])}
                                    {/if}
                                </div>
                            </div>
                        {/if}
                    {/each}
                </div>
            </div>
        {/each}
    {/if}
</FormBlock>
